import { useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { byTitle, ListRow, toRow } from './listFilters';

/**
 * What the catalog list is drawn from: every component and every API, and the
 * refs the reader owns through.
 *
 * One read, and every tab, chip and count is taken from it in the browser. The
 * list is the size of a platform's inventory, not of a search index, and
 * counting from one answer is what keeps "Mine 3" and the three rows under it
 * from ever disagreeing — two questions asked a moment apart can.
 *
 * Two requests rather than one with two kinds in it, because that is how the
 * sidebar counts the same list (useNavCounts), and the number beside Catalog
 * has to be the number on the All tab.
 */

const FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.description',
  'metadata.tags',
  'spec.type',
  'spec.lifecycle',
  'spec.owner',
  'spec.system',
  'relations',
];

export type CatalogList = {
  rows: ListRow[];
  /** The refs the reader owns through, lower-cased. */
  ownership: string[];
};

export type CatalogListState = {
  loading: boolean;
  error?: Error;
  value?: CatalogList;
};

/**
 * The last answer, for as long as the page is open and tied to who asked.
 *
 * A reader opens a component from the list and comes back to it, again and
 * again; without this the list blanks out and re-arrives each time, and the
 * row they were on moves while they look for it. It shows what was true a
 * moment ago while the request runs again. Kept per identity, because the
 * catalog answers per reader and the previous reader's list is not this one's.
 */
let lastKnown: { reader?: string; value?: CatalogList } = {};

export function useCatalogList(): CatalogListState {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [state, setState] = useState<CatalogListState>({ loading: true });

  useEffect(() => {
    let active = true;
    (async () => {
      const { userEntityRef, ownershipEntityRefs } =
        await identityApi.getBackstageIdentity();
      if (active && lastKnown.reader === userEntityRef && lastKnown.value) {
        setState({ loading: false, value: lastKnown.value });
      }
      const [components, apis] = await Promise.all([
        catalogApi.getEntities({
          filter: { kind: 'Component' },
          fields: FIELDS,
        }),
        catalogApi.getEntities({ filter: { kind: 'API' }, fields: FIELDS }),
      ]);
      const value: CatalogList = {
        rows: [...components.items, ...apis.items].map(toRow).sort(byTitle),
        ownership: (ownershipEntityRefs ?? []).map(ref =>
          ref.toLocaleLowerCase('en-US'),
        ),
      };
      lastKnown = { reader: userEntityRef, value };
      if (active) setState({ loading: false, value });
    })().catch(error => {
      // A list already on screen stays there; the reader is told only when
      // there is nothing to show instead.
      if (active) {
        setState(previous =>
          previous.value ? previous : { loading: false, error: error as Error },
        );
      }
    });
    return () => {
      active = false;
    };
  }, [catalogApi, identityApi]);

  return state;
}

/** Forget the last answer. For tests, which each sign in afresh. */
export function resetCatalogListCache() {
  lastKnown = {};
}
